import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Linkedin, ArrowUpRight, Plus, X } from "lucide-react";

/**
 * TeamShowcase — premium 2×3 team card grid.
 *
 * • Left-aligned content, no oversized initials.
 * • Concise bio by default; "Read profile" click reveals full bio in a modal.
 * • Non-localized data (accent, linkedin) lives here.
 * • Localized data (name, role, bio, tags) flows from i18n.
 */

export const TEAM = [
    { accent: "cyan",   linkedin: "https://www.linkedin.com/in/gabriel-teliceanu-2b014711a/" },
    { accent: "violet", linkedin: "https://www.linkedin.com/in/md-ibrahim-shopon-9b403550/" },
    { accent: "cyan",   linkedin: "https://www.linkedin.com/in/williamfgomes/" },
    { accent: "violet", linkedin: "https://www.linkedin.com/in/anand-sarbabidya-248b7116b/" },
    { accent: "cyan",   linkedin: "https://www.linkedin.com/in/imran-hossain-shakil/" },
    { accent: "violet", linkedin: "https://www.linkedin.com/in/md-shozibul-islam-7712671a1/" },
];

/* ---------- Short-bio helper: fixed word budget for uniform card heights ---------- */
const shortBio = (bio) => {
    if (!bio) return "";
    const words = bio.trim().split(/\s+/);
    // 24-word cap so all 6 cards use the same ~4 visible lines
    if (words.length <= 24) return words.join(" ");
    return words.slice(0, 24).join(" ").replace(/[.,;:]$/, "") + "…";
};

/* ---------- Signal glyph (abstract identity, no initials) ---------- */
const IdentityGlyph = ({ accent = "cyan", index = 0 }) => {
    const color = accent === "cyan" ? "rgba(0,229,255,0.75)" : "rgba(180,120,255,0.75)";
    const soft = accent === "cyan" ? "rgba(0,229,255,0.18)" : "rgba(180,120,255,0.18)";
    // Deterministic-ish variation per index (0..5)
    const seed = index + 1;
    return (
        <svg viewBox="0 0 80 80" width="52" height="52" aria-hidden="true" className="shrink-0">
            <defs>
                <radialGradient id={`glow-${index}`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={soft} />
                    <stop offset="100%" stopColor="transparent" />
                </radialGradient>
            </defs>
            <circle cx="40" cy="40" r="34" fill={`url(#glow-${index})`} />
            <circle cx="40" cy="40" r="26" fill="none" stroke={color} strokeOpacity="0.65" strokeWidth="1" />
            <circle cx="40" cy="40" r="10" fill="none" stroke={color} strokeOpacity="0.9" strokeWidth="1" />
            {/* 3 orbital dots offset by seed */}
            {[0, 1, 2].map((k) => {
                const angle = ((seed * 37 + k * 120) * Math.PI) / 180;
                const cx = 40 + Math.cos(angle) * 26;
                const cy = 40 + Math.sin(angle) * 26;
                return <circle key={k} cx={cx} cy={cy} r="2" fill={color} />;
            })}
        </svg>
    );
};

/* ---------- Card ---------- */
const TeamCard = ({ member, index, onOpen }) => {
    const glowColor =
        member.accent === "cyan" ? "rgba(0,229,255,0.10)" : "rgba(110,60,255,0.10)";
    const sweepClass =
        member.accent === "cyan"
            ? "from-cyan/60 via-cyan/30 to-transparent"
            : "from-violet/60 via-violet/30 to-transparent";
    return (
        <motion.div
            data-testid={`team-card-${index}`}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="group relative h-full border border-white/[0.08] bg-graphite p-8 md:p-10 overflow-hidden flex flex-col"
        >
            {/* hover radial glow */}
            <motion.div
                aria-hidden="true"
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(ellipse at top right, ${glowColor}, transparent 65%)` }}
            />
            {/* top edge sweep line */}
            <motion.span
                aria-hidden="true"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 + (index % 3) * 0.08 }}
                className={`absolute left-0 top-0 h-px w-full origin-left bg-gradient-to-r ${sweepClass}`}
            />

            {/* Profile visual + LinkedIn */}
            <div className="relative flex items-start justify-between gap-4 min-h-[56px]">
                <IdentityGlyph accent={member.accent} index={index} />
                {member.linkedin && (
                    <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`LinkedIn — ${member.name}`}
                        data-testid={`team-${index}-linkedin`}
                        className="inline-flex items-center justify-center w-9 h-9 border border-white/[0.1] bg-white/[0.02] text-white/70 hover:text-white hover:border-cyan/50 hover:bg-cyan/[0.05] transition-all"
                    >
                        <Linkedin className="w-4 h-4" strokeWidth={2} />
                    </a>
                )}
            </div>

            {/* Name — reserved height so long names don't push content */}
            <h3
                data-testid={`team-${index}-name`}
                className="relative mt-8 font-cabinet font-bold text-white text-2xl md:text-[26px] tracking-tight leading-[1.15] min-h-[62px]"
            >
                {member.name}
            </h3>

            {/* Role — reserved height for consistent alignment across cards */}
            <div className="relative mt-2 font-mono uppercase text-cyan text-[11.5px] tracking-[0.18em] font-semibold leading-[1.5] min-h-[52px]">
                {member.role}
            </div>

            {/* Bio — clamped to 4 lines with reserved min-height for perfect card sync */}
            <p
                className="relative mt-5 font-plex text-white/75 text-[15px] leading-[1.6] overflow-hidden min-h-[96px]"
                style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                }}
            >
                {shortBio(member.bio)}
            </p>

            {/* Tags — reserved 2-row minimum height for alignment */}
            <div className="relative mt-6 flex flex-wrap gap-1.5 min-h-[68px] content-start">
                {(member.tags || []).slice(0, 3).map((tag) => (
                    <span
                        key={tag}
                        className="font-mono text-[10.5px] uppercase tracking-[0.14em] font-semibold text-white/90 border border-white/[0.3] bg-white/[0.02] px-2.5 py-1 hover:border-cyan/60 hover:text-white transition-colors"
                    >
                        {tag}
                    </span>
                ))}
            </div>

            {/* Read Profile — anchored to bottom via mt-auto */}
            <div className="relative mt-auto pt-6 border-t border-white/[0.06] flex items-center justify-between h-[62px]">
                <button
                    type="button"
                    onClick={() => onOpen(index)}
                    data-testid={`team-${index}-read`}
                    className="inline-flex items-center gap-2 font-mono text-[11.5px] uppercase tracking-[0.18em] font-semibold text-white/85 hover:text-cyan transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan/60"
                >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                    <span>{member.readProfile}</span>
                </button>
                <ArrowUpRight
                    className="w-3.5 h-3.5 text-white/25 group-hover:text-cyan group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                    strokeWidth={2}
                />
            </div>
        </motion.div>
    );
};

/* ---------- Full-profile modal ---------- */
const ProfileModal = ({ member, onClose }) => {
    const { t } = useTranslation();
    if (!member) return null;
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-md"
            onClick={onClose}
            data-testid="team-modal"
        >
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl border border-white/[0.1] bg-graphite p-8 md:p-10 max-h-[85vh] overflow-y-auto"
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={t("landing.team.close") || "Close"}
                    data-testid="team-modal-close"
                    className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 border border-white/[0.1] bg-white/[0.02] text-white/70 hover:text-white hover:border-cyan/50 hover:bg-cyan/[0.05] transition-all"
                >
                    <X className="w-4 h-4" strokeWidth={2} />
                </button>

                <div className="flex items-center gap-4">
                    <IdentityGlyph accent={member.accent} index={member.__index || 0} />
                    <div>
                        <h3 className="font-cabinet font-bold text-white text-3xl md:text-[34px] tracking-tight leading-[1.05]">
                            {member.name}
                        </h3>
                        <div className="mt-2 font-mono uppercase text-cyan text-[12px] tracking-[0.18em] font-semibold">
                            {member.role}
                        </div>
                    </div>
                </div>

                <p className="mt-6 font-plex text-white/80 text-[16px] leading-[1.75]">
                    {member.bio}
                </p>

                {(member.tags || []).length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                        {member.tags.map((tag) => (
                            <span
                                key={tag}
                                className="font-mono text-[11px] uppercase tracking-[0.16em] font-medium text-white/80 border border-white/[0.14] bg-white/[0.03] px-2.5 py-1.5"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                )}

                {member.linkedin && (
                    <div className="mt-8">
                        <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2.5 font-mono text-[12px] uppercase tracking-[0.18em] font-semibold text-white hover:text-cyan transition-colors border-b border-white/25 hover:border-cyan/60 pb-1.5"
                        >
                            <Linkedin className="w-4 h-4" strokeWidth={2} />
                            LinkedIn
                            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </a>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

/* ---------- Section ---------- */
export const TeamShowcase = () => {
    const { t } = useTranslation();
    const i18nMembers = t("landing.team.members", { returnObjects: true }) || [];
    const readProfile = t("landing.team.readProfile") || "Read profile";
    const members = TEAM.map((m, i) => ({
        ...m,
        ...(i18nMembers[i] || {}),
        readProfile,
    }));
    const [openIndex, setOpenIndex] = useState(null);

    return (
        <div data-testid="team-showcase" className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {members.map((m, i) => (
                    <TeamCard
                        key={i}
                        member={m}
                        index={i}
                        onOpen={setOpenIndex}
                    />
                ))}
            </div>

            <AnimatePresence>
                {openIndex !== null && (
                    <ProfileModal
                        member={{ ...members[openIndex], __index: openIndex }}
                        onClose={() => setOpenIndex(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default TeamShowcase;
